import React, { Suspense, memo } from "react";
import { Route, Routes, Navigate, Outlet } from "react-router-dom";
import LandingPage from "../pages/LoginPage";
import { getToken } from "../utils/api";
import { useSelector } from "react-redux";

// Lazy load pages
const HomePage = React.lazy(() => import("../pages/HomePage"));
const ParentHomePage = React.lazy(() => import("../pages/ParentHomePage"));
const PythonPage = React.lazy(() => import("../pages/PythonPage"));
const RegisterUser = React.lazy(() => import("./RegisterUser"));
const AdminPage = React.lazy(() => import("../pages/AdminPage"));
const ChildProgressPage = React.lazy(() => import("../pages/ChildProgressPage"));

// Check if user is authenticated
const PrivateRoutes = memo(() => {
	const auth = getToken();
	return auth ? <Outlet /> : <Navigate to="/" />;
});

// Route guard for admin-only pages
const AdminRoute = memo(() => {
	const { userRole } = useSelector((state) => state.user);

	return userRole === 'admin' ? <Outlet /> : <Navigate to="/home" />;
});

// Route guard for student/instructor pages
const StudentInstructorRoute = memo(() => {
	const { userRole } = useSelector((state) => state.user);

	// Admin can access everything
	if (userRole === 'admin') return <Outlet />;

	// Students and instructors can access these routes
	if (userRole === 'student' || userRole === 'instructor') return <Outlet />;

	// Parents get redirected to their homepage
	return <Navigate to="/parent-home" />;
});

// Route guard for parent pages
const ParentRoute = memo(() => {
	const { userRole } = useSelector((state) => state.user);

	// Admin can access everything
	if (userRole === 'admin') return <Outlet />;

	// Parents can access these routes
	if (userRole === 'parent') return <Outlet />;

	// Students and instructors get redirected to their homepage
	return <Navigate to="/home" />;
});

const Navigation = () => {
	const { userRole } = useSelector((state) => state.user);

	// Determine home route based on role
	const getHomeRoute = () => {
		if (!getToken()) return "/";
		return userRole === 'parent' ? "/parent-home" : "/home";
	};

	return (
		<Suspense fallback={<div>Loading...</div>}>
			<Routes>
				{/* Public routes */}
				<Route path="/" element={<LandingPage />} />

				{/* Default redirect to appropriate home */}
				<Route path="*" element={<Navigate to={getHomeRoute()} />} />

				{/* Private routes - all require authentication */}
				<Route element={<PrivateRoutes />}>
					{/* Student/Instructor Routes */}
					<Route element={<StudentInstructorRoute />}>
						<Route path="/home" element={<HomePage />} />
						<Route path="/python" element={<PythonPage />} />
						{/* Add other lesson materials here */}
					</Route>

					{/* Parent Routes */}
					<Route element={<ParentRoute />}>
						<Route path="/parent-home" element={<ParentHomePage />} />
						<Route path="/child-progress/:userId" element={<ChildProgressPage />} />
					</Route>

					{/* Admin-specific Routes */}
					<Route element={<AdminRoute />}>
						<Route path="/admin-register-user" element={<RegisterUser />} />
						<Route path="/admin" element={<AdminPage />} />
					</Route>
				</Route>
			</Routes>
		</Suspense>
	);
};

export default Navigation;